/**
 * 子账号相关的 TanStack Query Hooks
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query'
import {
  getSubAccountList,
  createSubAccount,
  deleteSubAccount,
  updateSubAccountStatus,
} from '@/services/subAccountService'
import type {
  SubAccount,
  SubAccountListParams,
  SubAccountListResponse,
  CreateSubAccountRequest,
  CreateSubAccountResponse,
  DeleteSubAccountResponse,
  UpdateSubAccountStatusRequest,
  UpdateSubAccountStatusResponse,
} from '@/types/subAccount'

/** 子账号列表查询 Key */
const SUB_ACCOUNTS_QUERY_KEY = ['sub-accounts'] as const

/**
 * 获取子账号列表的 Hook
 * @param params 查询参数
 * @returns 子账号列表查询结果
 */
export function useSubAccounts(
  params?: SubAccountListParams
): UseQueryResult<SubAccountListResponse> {
  return useQuery({
    queryKey: [...SUB_ACCOUNTS_QUERY_KEY, params] as const,
    queryFn: async (): Promise<SubAccountListResponse> => {
      return await getSubAccountList(params)
    },
  })
}

/**
 * 创建子账号的 Hook
 * @returns 创建子账号 mutation
 */
export function useCreateSubAccount(): UseMutationResult<
  CreateSubAccountResponse,
  Error,
  CreateSubAccountRequest
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateSubAccountRequest): Promise<CreateSubAccountResponse> => {
      return await createSubAccount(data)
    },
    onSuccess: (): void => {
      // 创建成功后刷新子账号列表
      void queryClient.invalidateQueries({ queryKey: SUB_ACCOUNTS_QUERY_KEY })
    },
  })
}

/**
 * 删除子账号的 Hook
 * @returns 删除子账号 mutation
 */
export function useDeleteSubAccount(): UseMutationResult<
  DeleteSubAccountResponse,
  Error,
  string
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string): Promise<DeleteSubAccountResponse> => {
      return await deleteSubAccount(id)
    },
    onSuccess: (): void => {
      // 删除成功后刷新子账号列表
      void queryClient.invalidateQueries({ queryKey: SUB_ACCOUNTS_QUERY_KEY })
    },
  })
}

/**
 * 更新子账号状态的参数接口
 */
interface UpdateStatusParams {
  /** 子账号 ID */
  id: string
  /** 更新状态请求参数 */
  data: UpdateSubAccountStatusRequest
}

/**
 * 更新子账号状态的 Hook
 * @returns 更新子账号状态 mutation
 */
export function useUpdateSubAccountStatus(): UseMutationResult<
  UpdateSubAccountStatusResponse,
  Error,
  UpdateStatusParams
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: UpdateStatusParams): Promise<UpdateSubAccountStatusResponse> => {
      return await updateSubAccountStatus(params.id, params.data)
    },
    onSuccess: (): void => {
      // 更新状态成功后刷新子账号列表
      void queryClient.invalidateQueries({ queryKey: SUB_ACCOUNTS_QUERY_KEY })
    },
  })
}

export { SUB_ACCOUNTS_QUERY_KEY }
export type { SubAccount, SubAccountListParams }
